import { migrateExampleSupport } from './motif-migration.mjs'

// Normalize only classification identifiers. Document/node/edge IDs stay stable.
const copy = (value) => JSON.parse(JSON.stringify(value))
export const numericId = (id) => Number.isSafeInteger(id) && id > 0
export function normalizeBundle(input) {
  if (!input || !Array.isArray(input.schemas) || !Array.isArray(input.links)) return input
  const value = copy(input)
  const convert = (list) => {
    if (
      list.some(
        (item) =>
          !item ||
          !(
            numericId(item.id) ||
            (typeof item.id === 'string' && /^(?:[1-9][0-9]*|[a-z][a-z0-9_]*)$/.test(item.id))
          ),
      ) ||
      new Set(list.map((item) => String(item.id))).size !== list.length
    )
      throw new Error('Schema 文件格式无效。')
    const used = new Set(list.map((s) => Number(s.id)).filter(numericId))
    const map = new Map()
    let next = 1
    for (const type of list) {
      const previous = type.id
      let id = Number(previous)
      if (!numericId(id)) {
        while (used.has(next)) next++
        id = next++
        used.add(id)
        type.legacyId ??= previous
      }
      map.set(previous, id)
      if (type.legacyId) map.set(type.legacyId, id)
      type.id = id
    }
    return map
  }
  const nodes = convert(value.schemas),
    links = convert(value.links)
  const document = (doc) => {
    if (!doc) return
    for (const node of [...doc.paragraphs, ...doc.units]) node.type = nodes.get(node.type) ?? node.type
    for (const edge of doc.edges) edge.type = links.get(edge.type) ?? edge.type
  }
  document(value.document)
  Object.values(value.documents || {}).forEach(document)
  for (const motif of value.motifs || []) {
    if (motif.linkType) motif.linkType = links.get(motif.linkType) ?? motif.linkType
    if (motif.nodeTypes)
      for (const slot of Object.keys(motif.nodeTypes))
        motif.nodeTypes[slot] = nodes.get(motif.nodeTypes[slot]) ?? motif.nodeTypes[slot]
    if (motif.edgeTypes) motif.edgeTypes = motif.edgeTypes.map((id) => links.get(id) ?? id)
  }
  for (const draft of Object.values(value.drafts || {})) {
    if (draft.bundle) draft.bundle = normalizeBundle(draft.bundle)
    if (draft.base) draft.base = normalizeBundle(draft.base)
  }
  return migrateExampleSupport(value)
}
export function exportSchemas(bundle) {
  return {
    format: 'argumotif-schema',
    version: 1,
    ...Object.fromEntries(['schemas', 'links', 'facets', 'motifs'].map((key) => [key, copy(bundle[key])])),
  }
}
export function importSchemas(current, input) {
  if (
    !input ||
    (input.format && input.format !== 'argumotif-schema') ||
    (input.format && input.version !== 1) ||
    !Array.isArray(input.schemas) ||
    !Array.isArray(input.links)
  )
    throw new Error('Schema 文件格式无效。')
  const incoming = normalizeBundle({ ...input, facets: input.facets || [], motifs: input.motifs || [] })
  const result = exportSchemas(current)
  const merge = (key, numeric) => {
    const map = new Map(),
      list = result[key]
    for (const item of incoming[key]) {
      const same = list.find(
        (s) => s.id === item.id && s.name === item.name && (!numeric || s.english === item.english),
      )
      let id = item.id
      if (!same && list.some((s) => s.id === id))
        id = numeric ? Math.max(0, ...list.map((s) => s.id)) + 1 : `${id}_${list.length + 1}`
      map.set(item.id, id)
      const next = { ...item, id }
      if (same) list.splice(list.indexOf(same), 1, next)
      else list.push(next)
    }
    return map
  }
  const nodes = merge('schemas', true)
  const links = merge('links', true)
  const facets = merge('facets', false)
  for (const motif of incoming.motifs) {
    if (motif.linkType) motif.linkType = links.get(motif.linkType) ?? motif.linkType
    if (motif.nodeTypes)
      for (const slot of Object.keys(motif.nodeTypes))
        motif.nodeTypes[slot] = nodes.get(motif.nodeTypes[slot]) ?? motif.nodeTypes[slot]
    if (motif.edgeTypes) motif.edgeTypes = motif.edgeTypes.map((id) => links.get(id) ?? id)
    if (motif.nodeFacets)
      for (const constraint of Object.values(motif.nodeFacets))
        constraint.id = facets.get(constraint.id) ?? constraint.id
  }
  merge('motifs', false)
  if (result.motifs.some((m) => m.linkType && !result.links.some((link) => link.id === m.linkType)))
    throw new Error('Schema 文件格式无效。')
  return result
}
